// « I am looking at this chat » — the panel's heartbeat (owner, 2026-10-18:
// « Don't ping if the owner is viewing that exact chat in the panel right now »).
//
// While a conversation is open AND the tab is visible: one beat on open, then
// every 20 s. Hidden tab or closed chat: the beats stop and a last « I left »
// goes out, so the WhatsApp ping resumes at once rather than after the 60 s
// the worker keeps a beat (lib/takeoverPing.ts, KV `viewing:<chat>:<email>`).
// A failed beat is ignored: the cost of a missed one is a ping too many.
//
// Guard: viewingHeartbeat.test.ts
import { useEffect } from 'react';
import { whatsappApi } from '@/lib/api';

export const HEARTBEAT_MS = 20_000;

type Beat = (chatId: string, viewing: boolean) => Promise<unknown>;

/**
 * The heartbeat, framework-free: returns a stop function. `doc` and the timer
 * functions are injected so the test drives visibility and time by hand.
 */
export function startViewingHeartbeat(chatId: string, {
  beat = (id, viewing) => whatsappApi.viewingChat(id, viewing),
  doc = typeof document !== 'undefined' ? document : null,
  every = HEARTBEAT_MS,
  setIntervalFn = (fn: () => void, ms: number) => window.setInterval(fn, ms),
  clearIntervalFn = (id: number) => window.clearInterval(id),
}: {
  beat?: Beat;
  doc?: Pick<Document, 'visibilityState' | 'addEventListener' | 'removeEventListener'> | null;
  every?: number;
  setIntervalFn?: (fn: () => void, ms: number) => number;
  clearIntervalFn?: (id: number) => void;
} = {}): () => void {
  let timer: number | null = null;
  let beating = false;
  const send = (viewing: boolean) => { beat(chatId, viewing).catch(() => {}); };
  const on = () => {
    if (beating) return;
    beating = true;
    send(true);
    timer = setIntervalFn(() => send(true), every);
  };
  const off = (tell: boolean) => {
    if (timer != null) clearIntervalFn(timer);
    timer = null;
    if (beating && tell) send(false);
    beating = false;
  };
  const sync = () => (doc?.visibilityState === 'hidden' ? off(true) : on());
  sync();
  doc?.addEventListener('visibilitychange', sync);
  return () => {
    doc?.removeEventListener('visibilitychange', sync);
    off(true);
  };
}

/** The hook: one heartbeat per open chat. `null` = no chat open. */
export function useViewingHeartbeat(chatId: string | null | undefined, enabled = true): void {
  useEffect(() => {
    if (!chatId || !enabled) return;
    return startViewingHeartbeat(chatId);
  }, [chatId, enabled]);
}
